import { AI_CHAT_SCHEMA_VERSION, type AiChatTranscript } from 'src/types';

import type { ChatItem } from '../context/ai-orchestrator-context';
import type { ChatMessage } from '../core/llm';

// ----------------------------------------------------------------------
// Conversation ⇄ stored transcript.
//
// Everything read back from the API is untrusted: it may have been written by
// an older build, or by a schema we no longer understand. So restoring runs
// through an explicit validation pass rather than a cast.
// ----------------------------------------------------------------------

export type Conversation = {
  items: ChatItem[];
  history: ChatMessage[];
  vault?: unknown;
};

/** Longest title we derive from the opening message. */
const MAX_TITLE_LENGTH = 60;

const TOOL_STATUSES: ReadonlySet<string> = new Set([
  'running',
  'done',
  'error',
  'denied',
  'rejected',
]);
const CONFIRMATION_STATES: ReadonlySet<string> = new Set(['pending', 'approved', 'rejected']);
const ERROR_MESSAGE_KEYS: ReadonlySet<string> = new Set(['generic', 'aborted', 'context-full']);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isString = (value: unknown): value is string => typeof value === 'string';

const isContent = (value: unknown): boolean =>
  isString(value) || (Array.isArray(value) && value.every(isRecord));

const isToolCall = (value: unknown): boolean =>
  isRecord(value) &&
  isString(value.id) &&
  isRecord(value.function) &&
  isString(value.function.name) &&
  isString(value.function.arguments);

/**
 * A conversation loaded from history has no live engine behind it, so states
 * that only make sense mid-turn are settled: a tool that was still running
 * never finished, and a confirmation still pending can no longer be answered.
 */
const settleItem = (item: ChatItem): ChatItem => {
  if (item.kind === 'tool' && item.status === 'running') {
    return { ...item, status: 'error' };
  }
  if (item.kind === 'confirmation' && item.state === 'pending') {
    return { ...item, state: 'rejected' };
  }
  return item;
};

const parseItem = (value: unknown): ChatItem | null => {
  if (!isRecord(value)) return null;

  const { id, kind } = value;
  if (!isString(id)) return null;

  switch (kind) {
    case 'user':
    case 'assistant':
      return isString(value.text) ? settleItem({ kind, id, text: value.text }) : null;

    case 'tools-loaded':
      return Array.isArray(value.names) && value.names.every(isString)
        ? settleItem({ kind, id, names: value.names as string[] })
        : null;

    case 'tool':
      return isString(value.toolName) && isString(value.status) && TOOL_STATUSES.has(value.status)
        ? settleItem({
            kind,
            id,
            toolName: value.toolName,
            status: value.status as Extract<ChatItem, { kind: 'tool' }>['status'],
          })
        : null;

    case 'confirmation':
      return isString(value.toolName) &&
        isString(value.state) &&
        CONFIRMATION_STATES.has(value.state)
        ? settleItem({
            kind,
            id,
            toolName: value.toolName,
            args: value.args,
            state: value.state as Extract<ChatItem, { kind: 'confirmation' }>['state'],
          })
        : null;

    case 'image':
      return isString(value.path) ? settleItem({ kind, id, path: value.path }) : null;

    case 'error':
      return isString(value.messageKey) && ERROR_MESSAGE_KEYS.has(value.messageKey)
        ? settleItem({
            kind,
            id,
            messageKey: value.messageKey as Extract<ChatItem, { kind: 'error' }>['messageKey'],
          })
        : null;

    default:
      return null;
  }
};

const parseMessage = (value: unknown): ChatMessage | null => {
  if (!isRecord(value)) return null;

  switch (value.role) {
    case 'system':
    case 'developer':
    case 'user':
      return isContent(value.content) ? (value as unknown as ChatMessage) : null;

    case 'assistant': {
      const hasContent =
        value.content === null || value.content === undefined || isContent(value.content);
      if (!hasContent) return null;
      if (value.tool_calls !== undefined) {
        if (!Array.isArray(value.tool_calls) || !value.tool_calls.every(isToolCall)) return null;
      }
      // An assistant turn with neither text nor tool calls carries nothing.
      if (!isContent(value.content) && !Array.isArray(value.tool_calls)) return null;
      return value as unknown as ChatMessage;
    }

    case 'tool':
      return isContent(value.content) && isString(value.tool_call_id)
        ? (value as unknown as ChatMessage)
        : null;

    default:
      return null;
  }
};

// ----------------------------------------------------------------------

export const toTranscript = (conversation: Conversation): AiChatTranscript => ({
  schema_version: AI_CHAT_SCHEMA_VERSION,
  items: conversation.items,
  history: conversation.history,
  ...(conversation.vault ? { vault: conversation.vault } : {}),
});

/** `null` when the snapshot is from an incompatible schema or has no content. */
export const parseTranscript = (transcript: AiChatTranscript): Conversation | null => {
  if (transcript.schema_version !== AI_CHAT_SCHEMA_VERSION) return null;

  const items = (Array.isArray(transcript.items) ? transcript.items : [])
    .map(parseItem)
    .filter((item): item is ChatItem => item !== null);

  if (!items.length) return null;

  const history = (Array.isArray(transcript.history) ? transcript.history : [])
    .map(parseMessage)
    .filter((message): message is ChatMessage => message !== null);

  return { items, history, vault: transcript.vault };
};

/**
 * Conversations are titled from the user's opening message, so the title is
 * always in the language they actually wrote in.
 */
export const deriveChatTitle = (items: ChatItem[]): string => {
  const opening = items.find((item) => item.kind === 'user');
  const text = opening?.kind === 'user' ? opening.text.trim().replace(/\s+/g, ' ') : '';
  if (!text) return '';
  return text.length > MAX_TITLE_LENGTH ? `${text.slice(0, MAX_TITLE_LENGTH).trim()}…` : text;
};
